import { Vehicle, VehicleStats } from "../../models";

export const LOW_FUEL_THRESHOLD = 25;

export function relativeTime(iso: string | undefined, now: number = Date.now()): string {
  if (!iso) return "—";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "—";
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ago`;
}

export type VehicleStatusKind =
  | "fresh"
  | "stale"
  | "no_gps"
  | "not_linked"
  | "unavailable"
  | "unknown";

export interface VehicleStatus {
  kind: VehicleStatusKind;
  label: string;
}

const REASON_LABELS: Record<string, VehicleStatus> = {
  vehicle_not_linked: { kind: "not_linked", label: "Not linked" },
  no_gps: { kind: "no_gps", label: "No GPS" },
  upstream_unavailable: { kind: "unavailable", label: "Unavailable" },
  no_schedule_today: { kind: "unavailable", label: "No schedule" },
};

export function deriveStatus(vehicle: Vehicle, now: number = Date.now()): VehicleStatus {
  // The status column is location-facing (it mirrors the map); fall back to stats.
  const primary = vehicle.location ?? vehicle.stats;
  if (!primary) return { kind: "unknown", label: "—" };
  if (!primary.available) {
    return REASON_LABELS[primary.reason ?? ""] ?? { kind: "unavailable", label: "Unavailable" };
  }
  if (primary.stale) return { kind: "stale", label: "Stale" };
  return { kind: "fresh", label: `Updated ${relativeTime(primary.fetchedAt, now)}` };
}

export function isLowFuel(stats: VehicleStats | undefined): boolean {
  return !!stats?.available && typeof stats.fuelPercent === "number" && stats.fuelPercent < LOW_FUEL_THRESHOLD;
}
